/**
 * Process Stripe Element Payment Actor
 *
 * Handles the confirmation of Stripe Elements payment and recording the payment.
 * This is used for QR mode payments where users enter card info manually.
 */

import { fromPromise } from "xstate";
import { z } from "zod";

/**
 * Input Schema (What this actor needs)
 */
const ProcessStripeElementPaymentInputSchema = z.object({
  paymentIntentId: z.string(),
  amount: z.number().positive(),
  consumerId: z.string().optional(),
  userId: z.string().optional(),
  appointmentBtIds: z.array(z.string()).optional(),
  nonAppointmentBtIds: z.array(z.string()).optional(),
  collectedBy: z.string().default("kiosk"),
});

type ProcessStripeElementPaymentInput = z.infer<
  typeof ProcessStripeElementPaymentInputSchema
>;

/**
 * Success Schema (What this actor returns on success)
 */
const ProcessStripeElementPaymentSuccessSchema = z.object({
  paymentIntentId: z.string(),
  partialPaymentId: z.string().optional(),
  success: z.boolean(),
});

type ProcessStripeElementPaymentSuccess = z.infer<
  typeof ProcessStripeElementPaymentSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const ProcessStripeElementPaymentErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "NETWORK_ERROR",
    "BACKEND_ERROR",
    "PAYMENT_RECORDING_FAILED",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  details: z.unknown().optional(),
});

type ProcessStripeElementPaymentError = z.infer<
  typeof ProcessStripeElementPaymentErrorSchema
>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Custom error class for stripe element payment
 */
export class StripeElementPaymentError extends Error {
  code:
    | "NETWORK_ERROR"
    | "BACKEND_ERROR"
    | "PAYMENT_RECORDING_FAILED"
    | "INVALID_INPUT"
    | "UNKNOWN_ERROR";
  details?: unknown;
  retryable: boolean;
  actorName: string;
  functionName: string;
  location?: string;

  constructor(
    message: string,
    code:
      | "NETWORK_ERROR"
      | "BACKEND_ERROR"
      | "PAYMENT_RECORDING_FAILED"
      | "INVALID_INPUT"
      | "UNKNOWN_ERROR",
    details?: unknown,
    location?: string,
  ) {
    super(message);
    this.name = "StripeElementPaymentError";
    this.code = code;
    this.details = details;
    this.retryable = code === "NETWORK_ERROR" || code === "BACKEND_ERROR";
    this.actorName = "processStripeElementPayment";
    this.functionName = "processStripeElementPayment";
    this.location = location;
  }
}

/**
 * Record partial payment helper function
 */
const recordPartialPayment = async (
  params: ProcessStripeElementPaymentInput,
): Promise<{ partialPaymentId?: string }> => {
  try {
    const response = await fetch("/api/payment/partial", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        amount: params.amount,
        collectedBy: params.collectedBy,
        stripePaymentId: params.paymentIntentId,
        userId: params.userId,
        paymentMethodType: "CARD",
        appointmentBtIds: params.appointmentBtIds,
        nonAppointmentBtIds: params.nonAppointmentBtIds,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new StripeElementPaymentError(
        errorData.error || "Failed to record payment",
        "PAYMENT_RECORDING_FAILED",
        errorData,
        "recordPartialPayment:141",
      );
    }

    const data = await response.json();
    return { partialPaymentId: data.id };
  } catch (error) {
    if (error instanceof StripeElementPaymentError) {
      throw error;
    }

    throw new StripeElementPaymentError(
      (error as Error).message || "Failed to record payment",
      "NETWORK_ERROR",
      error,
      "recordPartialPayment:156",
    );
  }
};

/**
 * Process Stripe Element Payment Actor Implementation
 */
const processStripeElementPaymentActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: ProcessStripeElementPaymentInput;
  }): Promise<ProcessStripeElementPaymentSuccess> => {
    // Validate input

    const validated = ProcessStripeElementPaymentInputSchema.safeParse(input);

    console.log("Validated input:", validated);

    if (!validated.success) {
      const errorData: ProcessStripeElementPaymentError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, {
        ...errorData,
        actorName: "processStripeElementPayment",
        location: "process-stripe-element-payment.actor.ts:184",
      });
      throw error;
    }

    try {
      // Record the payment in our system
      // const paymentResult = await recordPartialPayment(validated.data);

      return {
        paymentIntentId: validated.data.paymentIntentId,
        partialPaymentId: "", //paymentResult.partialPaymentId,
        success: true,
      };
    } catch (error) {
      if (error instanceof StripeElementPaymentError) {
        const errorData: ProcessStripeElementPaymentError = {
          code: error.code,
          message: error.message,
          retryable: error.retryable,
          details: error.details,
        };
        const err = new Error(errorData.message);
        Object.assign(err, {
          ...errorData,
          actorName: "processStripeElementPayment",
          functionName: "processStripeElementPayment",
          location: "process-stripe-element-payment.actor.ts:215",
        });
        throw err;
      }

      // Unknown error
      const errorData: ProcessStripeElementPaymentError = {
        code: "UNKNOWN_ERROR",
        message: (error as Error).message || "Unknown error",
        retryable: true,
      };
      const err = new Error(errorData.message);
      Object.assign(err, {
        ...errorData,
        actorName: "processStripeElementPayment",
        location: "process-stripe-element-payment.actor.ts:229",
      });
      throw err;
    }
  },
);

/**
 * Process Stripe Element Payment Actor with Metadata
 */
export const processStripeElementPaymentActor: ActorWithMetadata = {
  impl: processStripeElementPaymentActorImpl,
  metadata: {
    displayName: "processStripeElementPayment",
    description: "Process Stripe Elements payment and record in database",
    category: "data",
    retryable: true,
    timeout: 30000, // 30 seconds
    errorCodes: [
      "INVALID_INPUT",
      "NETWORK_ERROR",
      "BACKEND_ERROR",
      "PAYMENT_RECORDING_FAILED",
      "UNKNOWN_ERROR",
    ],
    inputSchema: ProcessStripeElementPaymentInputSchema,
    successSchema: ProcessStripeElementPaymentSuccessSchema,
    errorSchema: ProcessStripeElementPaymentErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as ProcessStripeElementPaymentSuccess;
      return {
        paymentIntentId: success.paymentIntentId,
        partialPaymentId: success.partialPaymentId,
        paymentStatus: "success",
      };
    },
    onError: (error: unknown) => {
      const err = error as ProcessStripeElementPaymentError;
      return {
        paymentStatus: "failure",
        paymentError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
